import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useState } from "react";

import CloudGrid from "@/components/Cloud/Grid";
import PreviewModal, { FilePreview } from "@/components/Cloud/PreviewModal";
import CleanLayout from "@/components/Layout/CleanLayout";
import { CloudType } from "@/types/cloud";
import { typedFile } from "@/utils/typedFile";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { getSession } from "next-auth/client";
import useSWR from "swr";

const CloudFilePage = (): React.ReactElement => {
  const router = useRouter();
  const { filename, download } = router.query;

  const { data, error } = useSWR(
    filename ? `/cloud/preview/${filename}` : null
  );
  const file = useMemo(() => data && typedFile(data), [data]);
  const isLoading = !data && !error;
  const previewModal = useDisclosure();
  const [previewModalClosed, setPreviewModalClosed] = useState(false);

  useEffect(() => {
    if (
      file?.type === CloudType.OTHER &&
      file?.mimetype == "application/pdf" &&
      !previewModal.isOpen &&
      !previewModalClosed
    )
      previewModal.onOpen();
  }, [previewModal, file]);

  return (
    <>
      <Head>
        <title>{file?.originalname} - Accred.eu</title>
      </Head>

      {file?.type === CloudType.OTHER && file?.mimetype == "application/pdf" ? (
        <>
          {previewModal?.isOpen && (
            <PreviewModal
              preview
              file={file}
              isOpen={previewModal.isOpen}
              onClose={() => {
                previewModal.onClose();
                setPreviewModalClosed(true);
              }}
            />
          )}
          <CloudGrid
            data={{
              files: [file],
              folders: [],
            }}
            onPreviewFile={previewModal.onOpen}
            preview
            p={8}
            pt={5}
          />
        </>
      ) : (
        <Flex
          alignItems="center"
          flexDir="column"
          px={{ base: 0, md: "2rem" }}
          mx="auto"
          maxH="calc(100vh - 12rem)"
          h="calc(100vh - 12rem)"
          py="2rem"
        >
          <FilePreview
            isWebsite
            file={file}
            isLoading={isLoading}
            download={!!download}
          />
        </Flex>
      )}
    </>
  );
};

CloudFilePage.getLayout = function getLayout(page: React.ReactElement) {
  return <CleanLayout>{page}</CleanLayout>;
};

export const getServerSideProps: GetServerSideProps = async (ctx) => ({
  props: {
    ...(await serverSideTranslations(ctx.locale, ["common"])),
    session: await getSession(ctx),
  },
});

export default CloudFilePage;
